import {Button} from '@wandb/components';
import {LegacyWBIcon} from '@wandb/weave/common/components/elements/LegacyWBIcon';
import {
  getHREFFromAbsoluteURL,
  LinkNoCrawl,
} from '@wandb/weave/common/util/links';
import {
  applyPrefixIfNeeded,
  stripPrefixesIfNeeded,
} from '@wandb/weave/common/util/string';
import React, {FC, memo, useCallback, useState} from 'react';
import {useParams} from 'react-router-dom';
import {toast} from 'react-toastify';
// eslint-disable-next-line wandb/no-deprecated-imports
import {Image, List, StrictInputProps} from 'semantic-ui-react';

import githubIcon from '../assets/ic-github.svg';
import linkIcon from '../assets/ic-link.svg';
import locationIcon from '../assets/ic-location.svg';
import corporateOrganizationIcon from '../assets/ic-organization-corporate.svg';
import twitterIcon from '../assets/ic-twitter.svg';
import {useUpdateUserInfoMutation} from '../generated/graphql';
// eslint-disable-next-line import/no-cycle -- please fix if you can
import {ShareProfileTrigger} from '../pages/EntityPage';
import {useAccountTeamsList} from '../pages/HomePage/HomePageSidebar/useAccountTeamsList';
// eslint-disable-next-line import/no-cycle -- please fix if you can
import {ProfilePageQueryData} from '../pages/ProfilePage/ProfilePage';
import {TeamSidebarLink} from '../pages/Team/TeamSidebar.styles';
import {useViewer} from '../state/viewer/hooks';
import {getErrMsgFromError} from '../util/errors';
import {trackLaunchClicked} from '../util/navigation';
import {useRampFlagAccountSelector} from '../util/rampFeatureFlags';
import * as urls from '../util/urls';
import EditableImage from './EditableImage';
import {useAccountOrganizationOrPersonalEntityUsingLaunch} from './Launch/launchActive';
import * as S from './ProfileSidebar.styles';

type ProfileSidebarProps = {
  data: ProfilePageQueryData['user'];
  isEditable: boolean;
};

export const ProfileSidebar: FC<ProfileSidebarProps> = memo(
  ({data, isEditable}) => {
    const {userInfo, id} = data;
    const {entityName} = useParams<{entityName: string}>();

    const {isLaunchActive} =
      useAccountOrganizationOrPersonalEntityUsingLaunch();

    const enableAccountSelector = useRampFlagAccountSelector();
    const {accountTeams} = useAccountTeamsList();
    const viewer = useViewer();
    const isCurrentViewerProfilePage =
      viewer?.username && entityName === viewer.username;

    const showLaunch = isCurrentViewerProfilePage && isLaunchActive;

    const teamsData =
      enableAccountSelector && isCurrentViewerProfilePage
        ? accountTeams
        : data.teams;
    const [updateUser] = useUpdateUserInfoMutation();

    const [editing, setEditing] = useState(false);
    const [saving, setSaving] = useState(false);
    const [name, setName] = useState(data.name ?? ``);
    const [bio, setBio] = useState(userInfo?.bio ?? ``);
    const [company, setCompany] = useState(userInfo?.company ?? ``);
    const [location, setLocation] = useState(userInfo?.location ?? ``);
    const [websiteUrl, setWebsiteUrl] = useState(
      stripWebsitePrefixes(userInfo?.websiteUrl ?? ``)
    );
    const [twitterUrl, setTwitterUrl] = useState(
      stripTwitterPrefixes(userInfo?.twitterUrl ?? ``)
    );
    const [githubUrl, setGithubUrl] = useState(
      stripGithubPrefixes(userInfo?.githubUrl ?? ``)
    );

    const onCancelEdit = useCallback(() => {
      setEditing(false);
      setName(data.name ?? ``);
      setBio(userInfo?.bio ?? ``);
      setCompany(userInfo?.company ?? ``);
      setLocation(userInfo?.location ?? ``);
      setWebsiteUrl(stripWebsitePrefixes(userInfo?.websiteUrl ?? ``));
      setTwitterUrl(stripTwitterPrefixes(userInfo?.twitterUrl ?? ``));
      setGithubUrl(stripGithubPrefixes(userInfo?.githubUrl ?? ``));
    }, [data, userInfo]);

    const save = useCallback(async () => {
      setSaving(true);

      const websiteUrlWithoutPrefix = stripWebsitePrefixes(websiteUrl);
      if (websiteUrlWithoutPrefix !== websiteUrl) {
        setWebsiteUrl(websiteUrlWithoutPrefix);
      }
      const twitterUrlWithoutPrefix = stripTwitterPrefixes(twitterUrl);
      if (twitterUrlWithoutPrefix !== twitterUrl) {
        setTwitterUrl(twitterUrlWithoutPrefix);
      }
      const githubUrlWithoutPrefix = stripGithubPrefixes(githubUrl);
      if (githubUrlWithoutPrefix !== githubUrl) {
        setGithubUrl(githubUrlWithoutPrefix);
      }

      try {
        await updateUser({
          variables: {
            name,
            id,
            userInfo: JSON.stringify({
              ...userInfo,
              bio,
              company,
              location,
              websiteUrl: websiteUrlWithoutPrefix,
              twitterUrl: twitterUrlWithoutPrefix,
              githubUrl: githubUrlWithoutPrefix,
            }),
          },
        });
        setEditing(false);
      } catch (err) {
        const errMsg = getErrMsgFromError(err);
        console.error(errMsg);
        toast(
          `An error occurred while saving your profile: ${errMsg}. If this keeps happening, message support@wandb.com with a link to this page.`,
          {
            type: 'error',
          }
        );
      } finally {
        setSaving(false);
      }
    }, [
      updateUser,
      userInfo,
      id,
      name,
      bio,
      company,
      location,
      websiteUrl,
      twitterUrl,
      githubUrl,
    ]);

    const teams = (teamsData?.edges || [])
      .filter(team => team.node.isTeam)
      .map(team => {
        return (
          <List.Item key={team.node.name}>
            {team.node.photoUrl ? (
              <Image src={team.node.photoUrl} />
            ) : (
              <div className="no-profile-image">
                <S.TeamIcon />
              </div>
            )}
            <List.Content>
              <LinkNoCrawl to={`/${team.node.name}`}>
                {team.node.name}
              </LinkNoCrawl>
            </List.Content>
          </List.Item>
        );
      });

    return (
      <div className="profile-sidebar">
        <div className="inner-content">
          <EditableImage
            profile
            photoUrl={data.photoUrl}
            displaySize={200}
            label={false}
            readOnly={!isEditable}
            save={newVal => {
              updateUser({
                variables: {
                  photoUrl: newVal,
                },
              });
            }}
          />

          {!editing ? (
            <>
              <S.Name>{isEditable ? name ?? '' : name ?? data.username}</S.Name>

              <S.Username sub>
                {data.username}{' '}
                <ShareProfileTrigger entityName={data.username} />
              </S.Username>

              <S.Email sub>{data.email} </S.Email>

              <S.Bio>{bio}</S.Bio>

              {isEditable && (
                <Button
                  mt={6}
                  variant="secondary"
                  onPress={() => setEditing(true)}>
                  Edit profile
                </Button>
              )}

              <S.UserInfoList>
                {userInfo?.company && (
                  <S.UserInfoRow>
                    <S.UserInfoIcon src={corporateOrganizationIcon} />
                    <S.UserInfoValue>{userInfo.company}</S.UserInfoValue>
                  </S.UserInfoRow>
                )}
                {userInfo?.location && (
                  <S.UserInfoRow>
                    <S.UserInfoIcon src={locationIcon} />
                    <S.UserInfoValue>{userInfo.location}</S.UserInfoValue>
                  </S.UserInfoRow>
                )}
                {userInfo?.websiteUrl && (
                  <S.UserInfoRow>
                    <S.UserInfoIcon src={linkIcon} />
                    <S.UserInfoValue>
                      {stripWebsitePrefixes(userInfo.websiteUrl)}
                    </S.UserInfoValue>
                    <LinkNoCrawl
                      to={getHREFFromAbsoluteURL(userInfo.websiteUrl)}>
                      <S.LaunchIcon />
                    </LinkNoCrawl>
                  </S.UserInfoRow>
                )}
                {userInfo?.twitterUrl && (
                  <S.UserInfoRow>
                    <S.UserInfoIcon src={twitterIcon} />
                    <S.UserInfoValue>
                      {stripTwitterPrefixes(userInfo.twitterUrl)}
                    </S.UserInfoValue>
                    <LinkNoCrawl
                      to={applyPrefixIfNeeded(
                        `https://twitter.com/`,
                        userInfo.twitterUrl
                      )}>
                      <S.LaunchIcon />
                    </LinkNoCrawl>
                  </S.UserInfoRow>
                )}
                {userInfo?.githubUrl && (
                  <S.UserInfoRow>
                    <S.UserInfoIcon src={githubIcon} />
                    <S.UserInfoValue>
                      {stripGithubPrefixes(userInfo.githubUrl)}
                    </S.UserInfoValue>
                    <LinkNoCrawl
                      to={applyPrefixIfNeeded(
                        `https://github.com/`,
                        userInfo.githubUrl
                      )}>
                      <S.LaunchIcon />
                    </LinkNoCrawl>
                  </S.UserInfoRow>
                )}
              </S.UserInfoList>
            </>
          ) : (
            <>
              <S.EditRow>
                <S.EditLabel>Name</S.EditLabel>
                <S.EditInput
                  placeholder="Name"
                  value={name}
                  onChange={
                    ((e, {value}) =>
                      setName(value)) as StrictInputProps[`onChange`]
                  }
                />
              </S.EditRow>

              <S.EditRow>
                <S.EditLabel>Bio</S.EditLabel>
                <S.EditTextArea
                  placeholder="Bio"
                  value={bio}
                  onChange={
                    ((e, {value}) =>
                      setBio(value)) as StrictInputProps[`onChange`]
                  }
                />
              </S.EditRow>

              <S.UserInfoList>
                <S.UserInfoRow>
                  <S.UserInfoIcon src={corporateOrganizationIcon} />
                  <S.EditInput
                    placeholder="Your institution"
                    value={company}
                    onChange={
                      ((e, {value}) =>
                        setCompany(value)) as StrictInputProps[`onChange`]
                    }
                  />
                </S.UserInfoRow>
                <S.UserInfoRow>
                  <S.UserInfoIcon src={locationIcon} />
                  <S.EditInput
                    placeholder="Your location"
                    value={location}
                    onChange={
                      ((e, {value}) =>
                        setLocation(value)) as StrictInputProps[`onChange`]
                    }
                  />
                </S.UserInfoRow>
                <S.UserInfoRow>
                  <S.UserInfoIcon src={linkIcon} />
                  <S.EditInput
                    placeholder="Your website"
                    value={websiteUrl}
                    onChange={
                      ((e, {value}) =>
                        setWebsiteUrl(value)) as StrictInputProps[`onChange`]
                    }
                  />
                </S.UserInfoRow>
                <S.UserInfoRow>
                  <S.UserInfoIcon src={twitterIcon} />
                  <S.EditInput
                    placeholder="Twitter username"
                    value={twitterUrl}
                    onChange={
                      ((e, {value}) =>
                        setTwitterUrl(value)) as StrictInputProps[`onChange`]
                    }
                  />
                </S.UserInfoRow>
                <S.UserInfoRow>
                  <S.UserInfoIcon src={githubIcon} />
                  <S.EditInput
                    placeholder="GitHub username"
                    value={githubUrl}
                    onChange={
                      ((e, {value}) =>
                        setGithubUrl(value)) as StrictInputProps[`onChange`]
                    }
                  />
                </S.UserInfoRow>
              </S.UserInfoList>

              <S.EditActions>
                <Button onPress={save} isPending={saving}>
                  Save
                </Button>
                <Button
                  ml={4}
                  variant="ghost"
                  onPress={onCancelEdit}
                  isDisabled={saving}>
                  Cancel
                </Button>
              </S.EditActions>
            </>
          )}
          {showLaunch && (
            <S.ApplicationsContainer>
              <h3 className="subtitle">Applications</h3>
              <TeamSidebarLink
                data-test="launch"
                onClick={() => trackLaunchClicked('Profile sidebar')}
                to={urls.launchOverview()}
                className="see-all-link">
                <LegacyWBIcon name="rocket" size="large" />
                Launch
              </TeamSidebarLink>
            </S.ApplicationsContainer>
          )}
          {!data.hideTeamsFromPublic && (
            <>
              {teams.length ? <h3 className="subtitle">Teams</h3> : ''}
              <List className="user-team-list">{teams}</List>
            </>
          )}
        </div>
      </div>
    );
  }
);

function stripWebsitePrefixes(url: string): string {
  return stripPrefixesIfNeeded([`//`, `http://`, `https://`], url);
}

function stripTwitterPrefixes(url: string): string {
  return stripPrefixesIfNeeded(
    [
      `https://twitter.com/`,
      `https://www.twitter.com/`,
      `twitter.com/`,
      `www.twitter.com/`,
    ],
    url
  );
}

function stripGithubPrefixes(url: string): string {
  return stripPrefixesIfNeeded(
    [
      `https://github.com/`,
      `https://www.github.com/`,
      `github.com/`,
      `www.github.com/`,
    ],
    url
  );
}
